import React, { useEffect, useState } from 'react';
import "../../css/ResourcesTab.css";

const ResourcesTab = ({ course, activeLessonId }) => {
    const [resources, setResources] = useState([]);
    const [currentLessonTitle, setCurrentLessonTitle] = useState('');

    // Helper function to get file type from a file path or URL
    const getFileType = (source) => {
        if (!source) return 'File';

        // Strip query string and hash before reading the extension
        const clean = String(source).split('?')[0].split('#')[0];
        const extension = clean.split('.').pop().toLowerCase();

        const fileTypes = {
            'pdf': 'PDF',
            'zip': 'ZIP',
            'doc': 'DOC',
            'docx': 'DOCX',
            'ppt': 'PPT',
            'pptx': 'PPTX',
            'xls': 'XLS',
            'xlsx': 'XLSX',
            'txt': 'TXT',
            'jpg': 'JPG',
            'jpeg': 'JPEG',
            'png': 'PNG',
            'mp4': 'MP4',
            'mov': 'MOV',
            'avi': 'AVI'
        };
        return fileTypes[extension] || 'Link';
    };

    // Helper function to get icon based on file type
    const getResourceIcon = (source) => {
        if (!source) return '📄';

        const clean = String(source).split('?')[0].split('#')[0];
        const extension = clean.split('.').pop().toLowerCase();

        const iconMap = {
            'pdf': '📕',
            'zip': '📦',
            'doc': '📄',
            'docx': '📄',
            'ppt': '📊',
            'pptx': '📊',
            'xls': '📈',
            'xlsx': '📈',
            'txt': '📝',
            'jpg': '🖼️',
            'jpeg': '🖼️',
            'png': '🖼️',
            'mp4': '🎬',
            'mov': '🎬',
            'avi': '🎬'
        };
        return iconMap[extension] || '🔗';
    };

    // Helper function to format file size
    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / 1048576).toFixed(1) + ' MB';
    };

    // Helper function to get the full URL for a resource
    // - External URL (starts with http) → returned as-is
    // - Uploaded file path (relative)   → prefixed with the API base URL
    const getFileUrl = (filePath) => {
        if (!filePath) return '';
        const value = String(filePath);
        if (value.startsWith('http://') || value.startsWith('https://')) {
            return value;
        }
        const baseUrl =
            process.env.REACT_APP_API_PROD_URL ||
            process.env.REACT_APP_API_BASE_URL ||
            'http://127.0.0.1:8000';
        return `${baseUrl}${value.startsWith('/') ? '' : '/'}${value}`;
    };

    // Determine if a resource is a URL or a file
    const isUrlResource = (resource) => Boolean(resource.url);

    // Get the single source value for a resource (url preferred, then file)
    const getSource = (resource) => resource.url || resource.file || '';

    // Get resources for the active lesson
    useEffect(() => {
        if (!activeLessonId || !course.sections) {
            setResources([]);
            setCurrentLessonTitle('');
            return;
        }

        const activeResources = [];
        let lessonTitle = '';

        course.sections.forEach(section => {
            section.subsections?.forEach(subsection => {
                subsection.lessons?.forEach(lesson => {
                    if (lesson.id === activeLessonId) {
                        lessonTitle = lesson.title;

                        if (lesson.resources && Array.isArray(lesson.resources)) {
                            lesson.resources.forEach((resource) => {
                                activeResources.push({
                                    ...resource,
                                    lessonTitle: lesson.title,
                                    sectionTitle: section.title,
                                    subsectionTitle: subsection.title,
                                    lessonId: lesson.id
                                });
                            });
                        }
                    }
                });
            });
        });

        setResources(activeResources);
        setCurrentLessonTitle(lessonTitle);
    }, [activeLessonId, course.sections]);

    return (
        <div className="resources-tab-container">

            {/* Show header & summary ONLY if resources exist */}
            {activeLessonId && resources.length > 0 && (
                <>
                    <h3>Lesson Resources</h3>

                    <div className="resources-tab-summary">
                        <div className="resources-tab-summary-card">
                            <div className="resources-tab-summary-icon">📚</div>
                            <div className="resources-tab-summary-content">
                                {currentLessonTitle && (
                                    <p className="resources-tab-summary-subtitle">
                                        <strong>{currentLessonTitle} Learning materials</strong>
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Resources grid */}
            {activeLessonId && resources.length > 0 && (
                <div className="resources-tab-section">
                    <div className="resources-tab-grid">
                        {resources.map((resource, index) => {
                            const source = getSource(resource);
                            const link = getFileUrl(source);
                            const isUrl = isUrlResource(resource);

                            return (
                                <div key={resource.id || index} className="resources-tab-card">
                                    <div className="resources-tab-icon">
                                        {getResourceIcon(source)}
                                    </div>

                                    <div className="resources-tab-content">
                                        <div className="resources-tab-row">
                                            <h5>{resource.title || 'Untitled Resource'}</h5>

                                            <span className="resources-tab-type">
                                                {getFileType(source)}
                                            </span>

                                            {!isUrl && resource.file_size && (
                                                <span className="resources-tab-size">
                                                    {formatFileSize(resource.file_size)}
                                                </span>
                                            )}

                                            {link ? (
                                                <a
                                                    href={link}
                                                    className="resources-tab-download-btn"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    {...(isUrl ? {} : { download: true })}
                                                >
                                                    {isUrl ? 'Open Link' : 'Download'}
                                                </a>
                                            ) : (
                                                <span className="resources-tab-download-btn resources-tab-download-btn--disabled">
                                                    Unavailable
                                                </span>
                                            )}
                                        </div>

                                        {/* Show the URL when the resource is a link */}
                                        {isUrl && resource.url && (
                                            <div className="resources-tab-url">
                                                <span className="resources-tab-url-icon">🔗</span>
                                                <a
                                                    href={link}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="resources-tab-url-text"
                                                    title={resource.url}
                                                >
                                                    {resource.url}
                                                </a>
                                            </div>
                                        )}

                                        <div className="resources-tab-meta">
                                            <div className="resources-tab-section-info">
                                                {resource.sectionTitle} • {resource.subsectionTitle}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* No lesson selected */}
            {!activeLessonId && (
                <div className="resources-tab-empty">
                    <div className="resources-tab-empty-icon">📺</div>
                    <h4>Select a Lesson</h4>
                    <p>Click on a video lesson to see its resources here.</p>
                </div>
            )}

        </div>
    );
};

export default ResourcesTab;